import type { useLoaderData } from '@remix-run/react';
import { useMatches } from '@remix-run/react';
import invariant from '../tiny-invariant.js';

export type SerializeFrom<T> = ReturnType<typeof useLoaderData<T>>;

export function useRouteData<DataFunction>(routeId: string) {
  return useMatchesData<SerializeFrom<DataFunction>>(routeId, true);
}

export function useOptionalRouteData<DataFunction>(routeId: string) {
  return useMatchesData<SerializeFrom<DataFunction>>(routeId);
}

// overload 1 (like useRouteData)
// isRequired: true -> T
export function useMatchesData<T = Record<string, unknown>>(
  routeId: string,
  isRequired: true
): T;

// overload 2 (like useOptionalRouteData)
// isRequired: false | undefined -> T | undefined
export function useMatchesData<T = Record<string, unknown>>(
  routeId: string,
  isRequired?: false
): T | undefined;

// implementation (like existing useMatchesData)
// isRequired: boolean | undefined (default to false) -> T | undefined
export function useMatchesData<T = Record<string, unknown>>(
  routeId: string,
  isRequired = false
): T | undefined {
  const matchingRoutes = useMatches();
  const routeMatch = matchingRoutes.find((route) => route.id === routeId);

  // the root loader is always the first match in remix
  const rootMatch = matchingRoutes[0];
  const appContext = rootMatch?.data;
  const isMissingContext = typeof appContext === 'undefined';
  if (isMissingContext) {
    invariant(!isRequired, 'Missing app context');
    return undefined;
  }

  // early exit if they want `root`'s data since we already have it
  if (routeId === 'root') return appContext as T;

  return routeMatch?.data as T;
}
